import type { ReaderCatalogPrice } from '@/api/client';
import { contractorDisplayName } from '@/utils/contractorDisplayName';

/** Порядок строк каталога чтеца для отображения и сравнения */
export function sortReaderCatalogPrices(
  prices: ReaderCatalogPrice[] | undefined | null
): ReaderCatalogPrice[] {
  const list = prices ?? [];
  return [...list].sort((a, b) => {
    if (a.display_order !== b.display_order) return a.display_order - b.display_order;
    const ta = String(a.created_at ?? '');
    const tb = String(b.created_at ?? '');
    if (ta !== tb) return ta.localeCompare(tb);
    return String(a.id).localeCompare(String(b.id));
  });
}

export function formatReaderCatalogPriceLine(p: ReaderCatalogPrice): string {
  const n = Number(p.price);
  const priceStr = Number.isFinite(n)
    ? new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(n)
    : String(p.price);
  const name = p.price_name?.trim();
  return name ? `${name} — ${priceStr} ₽` : `${priceStr} ₽`;
}

/**
 * Форматирует цену: 1500 → "1 500 ₽"
 */
export function formatPrice(value: number | string | null | undefined): string {
  if (value == null || value === '') return '';
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(n)) return '';
  return `${Math.round(n).toLocaleString('ru-RU').replace(/\s/g, ' ')} ₽`;
}

/**
 * Отображение в поле ввода цены (PriceFixCombo): без «.00» у целых, без лишних нулей у дробных.
 */
export function formatPriceInputDisplay(value: number | string | null | undefined): string {
  if (value == null || value === '') return '';
  const n = typeof value === 'string' ? parseFloat(String(value).replace(',', '.')) : value;
  if (Number.isNaN(n)) return '';
  if (Number.isInteger(n)) return String(n);
  return String(n).replace(/\.?0+$/, '');
}

/** Как отрисовать ячейку стоимости чтеца в таблице проекта */
export type ReaderTablePriceVariant = 'amount' | 'not_selected' | 'na';

export function getReaderTableCells(r: {
  name: string;
  reader_time?: string | null;
  reader_custom_price?: number | null;
  reader_catalog_price_id?: string | null;
  reader_prices?: Array<{ id: string; price: number | string }>;
  effective_catalog_price?: number | string | null;
  reader_coef?: number | null;
  reader_edits_number?: number | null;
}): {
  name: string;
  /** Отформатированная сумма; пусто если не amount */
  price: string;
  priceVariant: ReaderTablePriceVariant;
  time: string;
  coef: string;
  /** Целое число правок; пусто если не задано */
  corrections: string;
} {
  const correctionsStr =
    r.reader_edits_number != null && Number.isFinite(Number(r.reader_edits_number))
      ? String(Math.trunc(Number(r.reader_edits_number)))
      : '';
  const catalog = r.reader_prices ?? [];
  const catalogCount = catalog.length;

  const customRaw = r.reader_custom_price;
  const hasCustom = customRaw != null && Number.isFinite(Number(customRaw));

  if (hasCustom) {
    let coef = '';
    if (r.reader_coef != null && Number.isFinite(Number(r.reader_coef))) {
      coef = formatDecimal(Number(r.reader_coef));
    }
    return {
      name: r.name,
      price: formatPrice(Number(customRaw)),
      priceVariant: 'amount',
      time: r.reader_time?.trim() ?? '',
      coef,
      corrections: correctionsStr,
    };
  }

  let priceVal: number | null = null;
  const cid = r.reader_catalog_price_id?.trim();
  if (cid && catalogCount > 0) {
    const row = catalog.find((p) => p.id === cid);
    if (row) {
      const n = Number(row.price);
      if (Number.isFinite(n)) priceVal = n;
    }
  }
  if (priceVal == null && r.effective_catalog_price != null) {
    const n = Number(r.effective_catalog_price);
    if (Number.isFinite(n)) priceVal = n;
  }
  if (priceVal == null && catalogCount === 1) {
    const row = catalog[0];
    const n = Number(row!.price);
    if (Number.isFinite(n)) priceVal = n;
  }

  let coef = '';
  if (r.reader_coef != null && Number.isFinite(Number(r.reader_coef))) {
    coef = formatDecimal(Number(r.reader_coef));
  }

  if (priceVal != null) {
    return {
      name: r.name,
      price: formatPrice(priceVal),
      priceVariant: 'amount',
      time: r.reader_time?.trim() ?? '',
      coef,
      corrections: correctionsStr,
    };
  }

  /** Несколько базовых позиций в каталоге и база для проекта не выбрана (или невалидный id) */
  if (catalogCount > 1) {
    return {
      name: r.name,
      price: '',
      priceVariant: 'not_selected',
      time: r.reader_time?.trim() ?? '',
      coef,
      corrections: correctionsStr,
    };
  }

  return {
    name: r.name,
    price: '',
    priceVariant: 'na',
    time: r.reader_time?.trim() ?? '',
    coef,
    corrections: correctionsStr,
  };
}

/** Строка для ячейки: Имя — цена — время (если есть) */
export function formatReaderDisplayLine(r: {
  name: string;
  reader_time?: string | null;
  reader_custom_price?: number | null;
  is_reader_price_fixed?: boolean | null;
  reader_catalog_price_id?: string | null;
  reader_prices?: Array<{ id: string; price: number | string }>;
  effective_catalog_price?: number | string | null;
}): string {
  const { name, price, priceVariant, time } = getReaderTableCells(r);
  const parts: string[] = [name];
  if (priceVariant === 'amount' && price) {
    parts.push(r.is_reader_price_fixed === true ? `ФИКС ${price}` : price);
  }
  if (priceVariant === 'not_selected') parts.push('Не выбрано');
  if (priceVariant === 'na') parts.push('N/A');
  if (time) parts.push(time);
  return parts.join(' — ');
}

export function getContractorTableCells(c: {
  legal_entity?: string | null;
  name?: string | null;
  service_name?: string | null;
  contract_name?: string | null;
  service_price?: number | null;
  catalog_price?: number | null;
  service_custom_price?: number | null;
  price?: number | null;
}): {
  name: string;
  service: string;
  price: string;
} {
  const n = c.price ?? c.service_custom_price ?? c.catalog_price ?? c.service_price;
  const priceStr = formatPrice(n);
  return {
    name: contractorDisplayName(c),
    service: (c.contract_name ?? c.service_name)?.trim() || '',
    price: priceStr,
  };
}

/**
 * Форматирует число: убирает лишние нули на конце.
 * x.00 → x, x.10 → x.1, x.12 → x.12
 */
export function formatDecimal(value: number | string | null | undefined): string {
  if (value == null || value === '') return 'N/A';
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(n)) return 'N/A';
  if (Number.isInteger(n)) return String(n);
  return String(n).replace(/\.?0+$/, '');
}
